import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CloseIcon, MyLocation } from '@yme/assets/icons';
import { Input } from '@yme/atoms/input';
import { SpinnerLoaderSizes } from '@yme/atoms/spinner-loader';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { getIsCartConvertedToOrder } from '@yme/common/redux/cart/selectors';
import { hasInputError, showErrorMessage, } from '@yme/components/CheckoutForm/ShippingDetailsForm/PreferredDealer/GoogleLocationSearch/SingleInputForm/helpers';
import { getCheckoutLabels } from '@yme/components/CheckoutForm/redux/selectors';
import { getDealerLocatorService } from '@yme/components/DealerLocator/redux/services';
import { useGeolocation, useOutsideClick } from '@yme/hooks/index';
import { getCurrentCountryRoot } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { DigitalDataMutation, setDigitalData } from '@yme/utils/common';
import { EVENT_NAME_MAP, keyCodes } from '@yme/utils/constants';
import { setGoogleSearchedPlace } from '../redux/actions';
import { getDealerLocatorSearchedPlace } from '../redux/selectors';
import { ControlPanelStyled, SingleInputFormStyled, SpinnerLoaderStyled, TargetWrapperStyled, } from './styled';
function SingleInputFormElement({ isRightToLeft, placeholder, setIsExpandedDropDown, fieldState, setFieldState, handleKeyDown, ready, onFocusHandler, isItemChosen, onClear, titleLabel, }) {
    const { language } = useTranslation();
    const [isClearIconVisible, setIsClearIconVisible] = useState(false);
    const [isInputError, setIsInputError] = useState(false);
    const [isTouched, setIsTouched] = useState(false);
    const dispatch = useDispatch();
    const targetRef = useRef(null);
    const currentCountry = useSelector(getCurrentCountryRoot);
    const searchedPlace = useSelector(getDealerLocatorSearchedPlace);
    const { checkout_personalDetailsRequiredField_error: requiredFieldError } = useSelector(getCheckoutLabels);
    const isCartConvertedToOrder = useSelector(getIsCartConvertedToOrder);
    const { geolocation, onGetGeolocation, isRequestingGeolocation, setIsRequestingGeolocation } = useGeolocation();
    const onBlurHandlerInputHandler = () => {
        if (!isItemChosen && isTouched) {
            setIsInputError(true);
        }
    };
    const { elementRef } = useOutsideClick({
        eventName: EVENT_NAME_MAP.click,
        callback: onBlurHandlerInputHandler,
    });
    const onFocusInputHandler = () => {
        setIsInputError(false);
        setIsTouched(true);
        onFocusHandler();
    };
    const onGeolocationHandler = useCallback(() => {
        if (searchedPlace?.latitude && searchedPlace?.longitude) {
            const parameters = `${searchedPlace.latitude}, ${searchedPlace.longitude}`;
            setFieldState(parameters);
        }
        else {
            onGetGeolocation();
        }
    }, [onGetGeolocation, searchedPlace.latitude, searchedPlace.longitude, setFieldState]);
    const onClearHandler = useCallback(() => {
        if (fieldState.description.length > 0) {
            onClear();
            setFieldState('');
            setIsExpandedDropDown(false);
        }
        dispatch(setGoogleSearchedPlace({
            latitude: null,
            longitude: null,
            value: null,
        }));
    }, [dispatch, fieldState, onClear, setFieldState, setIsExpandedDropDown]);
    const keyDownHandler = ({ callBack }) => (event) => {
        if (event && event.key === keyCodes.ENTER) {
            callBack();
        }
    };
    useEffect(() => {
        if (geolocation?.latitude && geolocation?.longitude) {
            const { latitude, longitude } = geolocation;
            const newInputValue = `${latitude}, ${longitude}`;
            if (geolocation.status === 'success') {
                setDigitalData(DigitalDataMutation.CHECKOUT_DEALER_SEARCH, {
                    location: 'customer_location',
                });
            }
            if (latitude && longitude) {
                const parameters = {
                    language,
                    coordinates: {
                        lat: latitude,
                        lng: longitude,
                    },
                    country: currentCountry,
                    dispatch,
                };
                setFieldState(newInputValue);
                dispatch(setGoogleSearchedPlace({
                    latitude,
                    longitude,
                    value: null,
                }));
                getDealerLocatorService(parameters);
            }
        }
        setIsRequestingGeolocation(false);
        setIsExpandedDropDown(false);
    }, [
        currentCountry,
        dispatch,
        geolocation,
        language,
        setFieldState,
        setIsExpandedDropDown,
        setIsRequestingGeolocation,
    ]);
    useEffect(() => {
        if (fieldState.description.length) {
            setIsClearIconVisible(true);
            setIsExpandedDropDown(true);
        }
        else {
            setIsClearIconVisible(false);
            setIsExpandedDropDown(false);
        }
    }, [fieldState, setIsExpandedDropDown]);
    const icons = (React.createElement(ControlPanelStyled, { isRightToLeft: isRightToLeft },
        isClearIconVisible && (React.createElement(SVGIcon, { type: CloseIcon, tabIndex: 0, size: ICON_SIZE.s, onClick: onClearHandler, keyDownHandler: keyDownHandler({
                callBack: onClearHandler,
            }) })),
        isRequestingGeolocation ? (React.createElement(SpinnerLoaderStyled, { isLoading: isRequestingGeolocation, size: SpinnerLoaderSizes.SMALL })) : (React.createElement(TargetWrapperStyled, { ref: targetRef },
            React.createElement(SVGIcon, { type: MyLocation, tabIndex: 0, size: ICON_SIZE.s, keyDownHandler: keyDownHandler({
                    callBack: onGeolocationHandler,
                }), onClick: onGeolocationHandler })))));
    return (React.createElement(SingleInputFormStyled, { isRightToLeft: isRightToLeft, ref: elementRef },
        React.createElement(Input, { changeHandler: setFieldState, id: "geolocation-field", name: "geolocation-field", placeholder: placeholder, isRightToLeft: isRightToLeft, value: fieldState.description, isDisabled: isRequestingGeolocation || !ready || isCartConvertedToOrder, onKeyDownHandler: handleKeyDown, focusHandler: onFocusInputHandler, label: titleLabel, state: hasInputError(isInputError), infoText: showErrorMessage(isInputError, requiredFieldError), multipleIconsBlock: !isCartConvertedToOrder && icons, isRequired: true })));
}
export const SingleInputForm = memo(SingleInputFormElement);
